import { useCallback, useState, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, AuthErrorDisplay, parseUrlError } from "@/auth";
import { Button } from "@shared-frontend/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@shared-frontend/components/ui/card";
import { Clock } from "lucide-react";

/**
 * Login page - initiates the Auth0 login flow
 *
 * Uses Auth0 directly for authentication, which then triggers:
 * 1. Platform token exchange (new - for backend APIs via AuthContext)
 * 2. After successful platform auth, user can also init Palantir OAuth if needed
 */
function LoginPage() {
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [sessionEndedReason, setSessionEndedReason] = useState<string | null>(null);

    // Platform auth state
    const {
        login,
        isAuthenticated: platformAuthenticated,
        isLoading,
        error: authError,
        pendingTenantSelection,
    } = useAuth();

    // Check for session ended reason (e.g., idle timeout)
    useEffect(() => {
        try {
            const reason = sessionStorage.getItem("session_ended_reason");
            if (reason) {
                setSessionEndedReason(reason);
                sessionStorage.removeItem("session_ended_reason");
            }
        } catch {
            // Ignore sessionStorage errors
        }
    }, []);

    // Check for error in URL params (from failed callback)
    const urlError = useMemo(
        () => parseUrlError(new URLSearchParams(window.location.search)),
        [],
    );
    const displayError = authError || urlError;

    const handleLogin = useCallback(async () => {
        setIsLoggingIn(true);
        try {
            // Start Auth0 login flow directly
            // AuthContext will handle token exchange after Auth0 callback
            await login();
        } catch (e: unknown) {
            console.error("Login error:", e);
            setIsLoggingIn(false);
        }
    }, [login]);

    // Handle keyboard shortcut
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter" && !isLoggingIn && !isLoading) {
                handleLogin();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleLogin, isLoggingIn, isLoading]);

    // Already authenticated - redirect to home
    if (platformAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Need to select tenant - redirect to selector
    if (pendingTenantSelection) {
        return <Navigate to="/select-tenant" replace />;
    }

    return (
        <div className="flex min-h-screen w-full items-center justify-center p-6">
            <Card className="w-full max-w-[500px]">
                <CardHeader className="space-y-3">
                    <div className="flex flex-col items-center space-y-4">
                        <CardTitle className="text-4xl font-brand">
                            HelmGuard
                        </CardTitle>
                        <CardDescription className="text-center text-sm text-foreground">
                            HelmGuard helps you quantify, monitor and mitigate
                            your enterprise risk with our AI agents.
                        </CardDescription>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col items-center space-y-4">
                        {sessionEndedReason === "idle_timeout" && (
                            <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2 text-amber-800">
                                <Clock className="h-4 w-4 flex-shrink-0" />
                                <span className="text-sm">
                                    Your session timed out due to inactivity. Please sign in again.
                                </span>
                            </div>
                        )}

                        {displayError && (
                            <div className="w-full">
                                <AuthErrorDisplay
                                    error={displayError}
                                    variant="inline"
                                    showReturnToLogin={false}
                                />
                            </div>
                        )}

                        <Button
                            className="w-48 bg-[hsl(var(--primary))] text-white hover:bg-"
                            onClick={handleLogin}
                            disabled={isLoggingIn}
                            variant="default"
                        >
                            {isLoggingIn ? (
                                <>Logging in...</>
                            ) : (
                                <>Sign in to HelmGuard</>
                            )}
                        </Button>
                        <div className="text-sm text-muted-foreground mt-2 text-center">
                            By entering the platform, you agree to our standard{" "}
                            <a
                                href="https://helmguard.ai/legal/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:underline"
                            >
                                terms and conditions
                            </a>
                            .
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default LoginPage;
